import React, { useState, useEffect, useRef } from 'react';
import './LiveChat.css';

const LiveChat = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'bot',
            text: "👋 Hi! I'm your ClinicPulse support assistant. I can help with:",
            options: ['Clinic setup & features', 'Pricing & demo requests', 'Getting started guides'],
            time: 'Just now'
        }
    ]);
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen, isTyping]);

    const toggleChat = () => setIsOpen(!isOpen);

    const handleOptionClick = (option) => {
        handleUserMessage(option);
    };

    const getAutomatedResponse = (userText) => {
        const text = userText.toLowerCase();
        if (text.includes('pricing') || text.includes('cost') || text.includes('demo')) {
            return "Our plans start at $49/month. Would you like to schedule a 15-minute demo with one of our clinic specialists?";
        }
        if (text.includes('setup') || text.includes('feature') || text.includes('start')) {
            return "ClinicPulse automates your entire workflow—from patient intake to billing. You can start with a 30-day free trial to explore all features.";
        }
        if (text.includes('guide') || text.includes('how') || text.includes('works')) {
            return "You can find our 'Getting Started' video series on the dashboard. It covers everything from adding your first patient to generating reports.";
        }
        if (text.includes('hello') || text.includes('hi')) {
            return "Hello! How can I assist you with ClinicPulse today?";
        }
        return "I've noted that! Our support team usually responds within 5 minutes. Is there anything specific you'd like to ask while you wait?";
    };

    const handleUserMessage = (text) => {
        const userMessage = {
            id: Date.now(),
            type: 'user',
            text: text,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, userMessage]);

        setIsTyping(true);
        setTimeout(() => {
            setIsTyping(false);
            const botResponse = {
                id: Date.now() + 1,
                type: 'bot',
                text: getAutomatedResponse(text),
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, botResponse]);
        }, 1200);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!inputValue.trim()) return;

        handleUserMessage(inputValue);
        setInputValue('');
    };

    return (
        <div className="live-chat-container">
            {/* Chat Trigger Button */}
            <button className="chat-trigger-btn pulse-primary animate-float" onClick={toggleChat}>
                {isOpen ? (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                ) : (
                    <>
                        <svg className="chat-icon" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" />
                            <circle cx="9" cy="9" r="2" />
                            <circle cx="15" cy="9" r="2" />
                        </svg>
                        <div className="notification-badge">1</div>
                        <div className="online-dot"></div>
                    </>
                )}
            </button>

            {/* Chat Window */}
            {isOpen && (
                <div className="chat-window">
                    <div className="chat-header">
                        <div className="chat-header-info">
                            <div className="header-icon-bg">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                                    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                                </svg>
                            </div>
                            <div>
                                <h3>Live Support</h3>
                                <div className="online-status-text">
                                    <span className="status-dot-small"></span>
                                    Online
                                </div>
                            </div>
                        </div>
                        <button className="close-btn" onClick={toggleChat} aria-label="Close chat">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>


                    <div className="chat-messages">
                        {messages.map((msg) => (
                            <div key={msg.id} className={`message-row ${msg.type === 'user' ? 'user-row' : ''}`}>
                                {msg.type === 'bot' && (
                                    <div className="avatar-container">
                                        <div className="avatar">CP</div>
                                    </div>
                                )}
                                <div className={`message-bubble ${msg.type === 'user' ? 'user-bubble' : ''}`}>
                                    {msg.text}
                                    {msg.options && (
                                        <div className="bubble-options">
                                            {msg.options.map((opt, i) => (
                                                <button key={i} className="option-btn" onClick={() => handleOptionClick(opt)}>
                                                    • {opt}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="message-row">
                                <div className="avatar">CP</div>
                                <div className="typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="chat-footer">
                        <form className="input-wrapper" onSubmit={handleSubmit}>
                            <input
                                type="text"
                                className="chat-input"
                                placeholder="Type your message..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                            />
                            <button type="submit" className="send-btn" aria-label="Send message">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                </svg>
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiveChat;
